import React, { useState, useEffect, useContext } from "react";
import * as func from "../../api/apiHelper";
import { downloadExport } from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faPenToSquare,
  faSearch,
  faEye,
  faClock,
  faCheckCircle,
  faHourglassHalf,
  faFlag,
  faSlidersH,
  faXmark,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ReportsContext } from "../context/reportsAppContext";

const STATUS_MAP = {
  OPEN: { cls: "bg-yellow-100 text-yellow-700", icon: faClock, label: "Open" },
  INPROGRESS: { cls: "bg-blue-100 text-blue-700", icon: faHourglassHalf, label: "In Progress" },
  CLOSED: { cls: "bg-green-100 text-green-700", icon: faCheckCircle, label: "Closed" },
};

const STATUS_CYCLE = { OPEN: "INPROGRESS", INPROGRESS: "CLOSED", CLOSED: "OPEN" };
const STATUS_NEXT_LABEL = { OPEN: "→ In Progress", INPROGRESS: "→ Close", CLOSED: "→ Reopen" };

const sampleFilterData = {
  status: "",
  matchseekerName: "",
  matchseekerMobile: "",
  startDate: "",
  endDate: "",
};

const Reports = () => {
  const {
    reportsSearchTerm: globalSearchTerm,
    setReportsSearchTerm: setGlobalSearchTerm,
    setReportsFilters: setGlobalFilters,
    reportsFilters: globalFilters,
    ContextCurrentPage: currentPage,
    contextSetCurrentPage: setCurrentPage,
    ContextTotalPages: totalPages,
    contextSetTotalPages: setTotalPages,
    Contextlimit: limit,
    ContextReload: reload,
    ContextSetReload: setReload,
  } = useContext(ReportsContext);

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editReportData, setEditReportData] = useState({});
  const [showEditButton, setShowEditButton] = useState(false);
  const [viewReportPopup, setViewReportPopup] = useState(false);
  const [viewReportData, setViewReportData] = useState({});
  const [cyclingId, setCyclingId] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const controller = new AbortController();
    const fetchReports = async () => {
      setLoading(true);
      const params = { page: currentPage, limit, searchTerm: globalSearchTerm };
      try {
        const res = await func.post(urls.GET_ALL_REPORTS_URL, globalFilters, { params, signal: controller.signal });
        if (res?.status === 200) {
          setReports(res?.data?.data || []);
          setTotalPages(res?.data?.pagination?.totalPages || 1);
        } else {
          toast.error("Failed to load reports");
        }
      } catch (err) {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        toast.error("Failed to load reports");
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
    return () => controller.abort();
  }, [reload, currentPage]);

  const handleSearch = () => { setReload(!reload); setCurrentPage(1); };

  const applyFilters = () => { setReload(!reload); setShowFilter(false); setGlobalSearchTerm(""); setCurrentPage(1); };

  const resetFilters = () => {
    setGlobalFilters(sampleFilterData);
    setGlobalSearchTerm("");
    setReload(!reload);
    setCurrentPage(1);
  };

  const handleCycleStatus = async (report) => {
    const nextStatus = STATUS_CYCLE[report.status] || "INPROGRESS";
    setCyclingId(report._id);
    try {
      const res = await func.put(urls.UPDATE_REPORT_STATUS_URL + report._id, { newStatus: nextStatus });
      if (res?.status === 200) {
        toast.success(`Status updated to ${nextStatus}`);
        setReload(!reload);
      } else {
        toast.warn("Failed to update status");
      }
    } catch { toast.error("Network error"); }
    setCyclingId(null);
  };

  const handleEditReport = async () => {
    setShowEditButton(true);
    if (!editReportData._id) { toast.warn("ID required"); setShowEditButton(false); return; }
    if (!editReportData.status) { toast.warn("Status required"); setShowEditButton(false); return; }
    const res = await func.put(urls.UPDATE_REPORT_STATUS_URL + editReportData._id, { newStatus: editReportData.status });
    if (res?.status === 200) {
      toast.success(res?.data?.message || "Status updated successfully");
      setShowEditPopup(false);
      setReload(!reload);
    } else {
      toast.warn("Failed to update status");
    }
    setShowEditButton(false);
  };

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1).filter(
    (p) => p >= currentPage - 2 && p <= currentPage + 2
  );

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 mt-14 rounded-xl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faFlag} className="text-white text-xl" />
            <div>
              <h1 className="text-2xl font-bold text-white">Reports</h1>
              <p className="text-white/70 text-sm">User-submitted reports and issues</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadExport('mst-admin/export/reports', { status: globalFilters.status }, 'reports_export.csv')}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition border border-white/20"
            >
              <FontAwesomeIcon icon={faDownload} />
              Export
            </button>
            <button
              onClick={() => setShowFilter(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition border border-white/20"
            >
              <FontAwesomeIcon icon={faSlidersH} />
              Filters
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1 max-w-sm">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={globalSearchTerm}
              onChange={(e) => { setGlobalSearchTerm(e.target.value); if (!e.target.value.trim()) handleSearch(); }}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              maxLength={60}
              placeholder="Search reports..."
              className="w-full pl-9 pr-3 py-2 text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 text-sm bg-white text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition shadow"
          >
            Search
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["#", "Match Seeker", "Status", "Created / Updated", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <tr key={i} className="border-b border-gray-100 animate-pulse">
                      {[...Array(5)].map((_, j) => (
                        <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-200 rounded" /></td>
                      ))}
                    </tr>
                  ))
                ) : reports.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-16 text-center text-gray-400">
                      <FontAwesomeIcon icon={faFlag} className="text-3xl mb-3 block mx-auto opacity-20" />
                      No reports found
                    </td>
                  </tr>
                ) : (
                  reports.map((report, i) => {
                    const statusCfg = STATUS_MAP[report?.status] || { cls: "bg-gray-100 text-gray-500", label: report?.status };
                    return (
                      <tr key={report._id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                        <td className="px-4 py-3 text-gray-400 text-xs">{(currentPage - 1) * limit + i + 1}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-800">{report.matchseekerDetails?.name || "—"}</p>
                          <p className="text-xs text-gray-400">{report?.matchseekerDetails?.mobile || ""}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${statusCfg.cls}`}>
                            {statusCfg.icon && <FontAwesomeIcon icon={statusCfg.icon} className="text-[10px]" />}
                            {statusCfg.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                          <p>{new Date(report.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</p>
                          <p className="text-gray-400">{new Date(report.updatedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => { setViewReportData(report); setViewReportPopup(true); }}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded-md font-medium transition"
                            >
                              <FontAwesomeIcon icon={faEye} />
                              View
                            </button>
                            <button
                              onClick={() => { setEditReportData(report); setShowEditPopup(true); }}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition"
                            >
                              <FontAwesomeIcon icon={faPenToSquare} />
                              Edit
                            </button>
                            <button
                              onClick={() => handleCycleStatus(report)}
                              disabled={cyclingId === report._id}
                              title={STATUS_NEXT_LABEL[report.status] || "Advance status"}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-md font-medium transition disabled:opacity-50 whitespace-nowrap"
                            >
                              {cyclingId === report._id
                                ? <FontAwesomeIcon icon={faHourglassHalf} className="animate-pulse" />
                                : STATUS_NEXT_LABEL[report.status] || "Advance"
                              }
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1 mt-5">
            <PagBtn onClick={() => setCurrentPage(1)} disabled={currentPage === 1} label="«" />
            <PagBtn onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} icon={faChevronLeft} />
            {currentPage > 3 && <span className="px-2 text-white/60">…</span>}
            {pages.map((p) => (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={`w-8 h-8 rounded-md text-sm font-medium transition ${
                  p === currentPage ? "bg-white text-indigo-700" : "text-white/70 hover:bg-white/10"
                }`}
              >
                {p}
              </button>
            ))}
            {currentPage < totalPages - 2 && <span className="px-2 text-white/60">…</span>}
            <PagBtn onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} icon={faChevronRight} />
            <PagBtn onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} label="»" />
          </div>
        )}
      </div>

      {/* Filter Modal */}
      {showFilter && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowFilter(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">Filter Reports</h3>
              <button onClick={() => setShowFilter(false)} className="text-gray-400 hover:text-gray-600 transition">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-6 py-4 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Matchseeker Name</label>
                <input type="text" value={globalFilters.matchseekerName} onChange={(e) => setGlobalFilters({ ...globalFilters, matchseekerName: e.target.value })} placeholder="Name" className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Matchseeker Mobile</label>
                <input type="text" value={globalFilters.matchseekerMobile} onChange={(e) => setGlobalFilters({ ...globalFilters, matchseekerMobile: e.target.value })} placeholder="Mobile" className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                <input type="date" value={globalFilters.startDate} onChange={(e) => setGlobalFilters({ ...globalFilters, startDate: e.target.value })} className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                <input type="date" value={globalFilters.endDate} onChange={(e) => setGlobalFilters({ ...globalFilters, endDate: e.target.value })} className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select value={globalFilters.status} onChange={(e) => setGlobalFilters({ ...globalFilters, status: e.target.value })} className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 bg-white">
                  <option value="">All Statuses</option>
                  <option value="OPEN">Open</option>
                  <option value="INPROGRESS">In Progress</option>
                  <option value="CLOSED">Closed</option>
                </select>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button onClick={resetFilters} className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">Reset</button>
              <button onClick={applyFilters} className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium">Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Status Modal */}
      {showEditPopup && editReportData && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowEditPopup(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">Update Report Status</h3>
              <button onClick={() => setShowEditPopup(false)} className="text-gray-400 hover:text-gray-600 transition">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-6 py-4">
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                value={editReportData?.status || ""}
                onChange={(e) => setEditReportData((prev) => ({ ...prev, status: e.target.value }))}
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 bg-white"
              >
                <option value="OPEN">Open</option>
                <option value="INPROGRESS">In Progress</option>
                <option value="CLOSED">Closed</option>
              </select>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button onClick={() => setShowEditPopup(false)} className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">Cancel</button>
              <button onClick={handleEditReport} disabled={showEditButton} className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium disabled:opacity-50">
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Report Modal */}
      {viewReportPopup && viewReportData && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setViewReportPopup(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">Report Details</h3>
              <button onClick={() => setViewReportPopup(false)} className="text-gray-400 hover:text-gray-600 transition">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-6 py-4 grid grid-cols-3 gap-4 text-sm">
              <InfoField label="Customer Name" value={viewReportData?.matchseekerDetails?.name} />
              <InfoField label="Mobile" value={viewReportData?.matchseekerDetails?.mobile} />
              <InfoField label="Status" value={viewReportData?.status} />
              <div className="col-span-3">
                <label className="block text-xs font-medium text-gray-500 mb-1">Message</label>
                <textarea value={viewReportData?.message || "—"} readOnly rows={4} className="w-full text-sm px-3 py-2 border border-gray-200 rounded-md bg-gray-50 resize-none" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100">
              <button onClick={() => setViewReportPopup(false)} className="w-full py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoField = ({ label, value }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    <div className="text-sm text-gray-800 bg-gray-50 px-3 py-2 rounded-md border border-gray-100">{value || "—"}</div>
  </div>
);

const PagBtn = ({ onClick, disabled, label, icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-8 h-8 flex items-center justify-center rounded-md text-sm transition ${
      disabled ? "text-white/20 cursor-not-allowed" : "text-white/70 hover:bg-white/10"
    }`}
  >
    {icon ? <FontAwesomeIcon icon={icon} className="text-xs" /> : label}
  </button>
);

export default Reports;
